/**
 * 扫码功能模块
 */
import { Html5Qrcode } from 'html5-qrcode';
import { detectWebView } from './webview-detection';
import { createAppMessageListener, addAppMessageListener, removeAppMessageListener } from './message-handler';
import { Modal, message } from 'antd';

const HTML5_QRCODE_ELEMENT_ID = 'html5-qrcode-scanner';

// 用于扫码的全局变量
let html5QrCode = null;

// 创建用于扫码的HTML元素
function createQrScannerElement() {
  let element = document.getElementById(HTML5_QRCODE_ELEMENT_ID);
  if (!element) {
    element = document.createElement('div');
    element.id = HTML5_QRCODE_ELEMENT_ID;
    element.style.position = 'fixed';
    element.style.top = '0';
    element.style.left = '0';
    element.style.width = '100%';
    element.style.height = '100%';
    element.style.zIndex = '9999';
    element.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
    element.style.display = 'flex';
    element.style.flexDirection = 'column';
    element.style.justifyContent = 'center';
    element.style.alignItems = 'center';
    element.innerHTML = '<div style="position:relative; width: 80%; max-width: 400px; background: white; padding: 20px; border-radius: 8px;">' +
                        '<div id="' + HTML5_QRCODE_ELEMENT_ID + '-reader" style="width: 100%;"></div>' +
                        '<button id="' + HTML5_QRCODE_ELEMENT_ID + '-cancel-btn" style="margin-top: 15px; padding: 10px 20px; background: #f44336; color: white; border: none; border-radius: 4px; cursor: pointer;">取消</button>' +
                        '</div>';
    document.body.appendChild(element);
  }
  return element;
}

// 显示扫码界面
function showQrScanner() {
  const element = createQrScannerElement();
  element.style.display = 'flex';
}

// 隐藏扫码界面
function hideQrScanner() {
  const element = document.getElementById(HTML5_QRCODE_ELEMENT_ID);
  if (element) {
    element.style.display = 'none';
  }
}

// 移除扫码界面
function removeQrScanner() {
  const element = document.getElementById(HTML5_QRCODE_ELEMENT_ID);
  if (element) {
    element.remove();
  }
}

/**
 * 扫码功能 - 统一处理二维码和条形码
 * @param {Object} options - 扫码选项
 * @param {string} options.type - 扫码类型 ('qr' 或 'barcode')，默认为 'both'
 * @param {number} options.timeout - 超时时间（毫秒），默认为 15000
 * @returns {Promise<string>} 扫码结果
 */
export function scanCode(options = {}) {
  const { type = 'qr', timeout = 15000 } = options;
  
  return new Promise((resolve, reject) => {
    const { isWebView } = detectWebView();
    
    if (isWebView && window.ReactNativeWebView) {
      // 在WebView中，通过postMessage与原生通信请求扫码
      const callbackId = 'scan_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      
      // 创建专门用于处理此请求的监听器
      let scanType = 'SCAN_CODE'; // 默认通用扫码
      const messageListener = createAppMessageListener(callbackId, resolve, reject, scanType + "_RESPONSE");
      addAppMessageListener(messageListener);
      
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: scanType,
        payload: {
          callbackId: callbackId
        }
      }));
      
      // 设置超时
      setTimeout(() => {
        // 如果超时，移除对应的消息监听器
        removeAppMessageListener(messageListener);
        reject(new Error('Scan code timeout'));
      }, timeout);
    } else {
      // 在普通浏览器中使用Html5Qrcode
      if (typeof Html5Qrcode !== 'undefined') {
        showQrScanner();
        
        const qrCodeSuccessCallback = (decodedText, decodedResult) => {
          // 成功扫描到码，停止扫描并清理
          if (html5QrCode) {
            html5QrCode.stop().then(ignore => {
              // 停止成功，隐藏界面并返回结果
              hideQrScanner();
              resolve({data: decodedText});
            }).catch(err => {
              // 停止失败，仍然隐藏界面并返回结果
              console.error('Failed to stop html5 qrcode scanner', err);
              hideQrScanner();
              resolve({data: decodedText});
            });
          }
        };
        
        // 根据扫码类型设置不同的配置
        let config;
        if (type === 'barcode') {
          // 条形码通常是长方形，调整框大小
          config = { fps: 15, aspectRatio: 1.0 };
        } else {
          // 默认使用正方形区域，适用于二维码
          config = { fps: 15, aspectRatio: 1.0 };
        }
        
        try {
          html5QrCode = new Html5Qrcode(HTML5_QRCODE_ELEMENT_ID + "-reader");
          
          // 启动摄像头扫描
          html5QrCode.start(
            { facingMode: "environment" },
            config,
            qrCodeSuccessCallback,
            () => {
              // 扫描失败或找不到码
            }
          ).catch(err => {
            console.error('Error starting html5 code scanner', err);
            hideQrScanner();
            reject(new Error('Could not start camera for code scanning: ' + err));
          });
          
          // 添加取消按钮事件监听
          const cancelButton = document.getElementById(HTML5_QRCODE_ELEMENT_ID + "-cancel-btn");
          if (cancelButton) {
            cancelButton.onclick = () => {
              if (html5QrCode) {
                html5QrCode.stop().then(ignore => {
                  hideQrScanner();
                  reject(new Error('User cancelled code scanning'));
                }).catch(err => {
                  console.error('Failed to stop html5 code scanner', err);
                  hideQrScanner();
                  reject(new Error('User cancelled code scanning'));
                });
              } else {
                hideQrScanner();
                reject(new Error('User cancelled code scanning'));
              }
            };
          }
        } catch (err) {
          console.error('Error initializing html5 code scanner', err);
          hideQrScanner();
          reject(new Error('Could not initialize code scanner: ' + err));
        }
      } else {
        // 如果Html5Qrcode不可用
        console.warn('Html5Qrcode is not available in this browser');
        reject(new Error('Code scanning is not supported in this browser'));
      }
    }
  });
}

// 为了保持向后兼容，提供别名函数
export const scanQRCode = (options = {}) => scanCode({...options, type: 'qr'});
export const scanBarcode = (options = {}) => scanCode({...options, type: 'barcode'});